import React, { useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import LastMinuteHero from './LastMinuteHero'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'

import { OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_LAST_MINUTE_SLUG } from 'constants/offer'
import { isLastMinuteEnabled } from 'selectors/lastMinute'
import Error404Page from 'components/Pages/Error404Page'
import HttpStatus from 'components/Common/HttpStatus'
import * as InteractionStudioService from 'api/interactionStudio'

import memoize from 'lib/memoize/memoize'
import { getPlacement } from 'lib/url/campaignUtils'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import { BANNER_LOCATION_LAST_MINUTE } from 'constants/bannerLocations'
import { fetchBanners } from 'actions/ContentActions'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import { frontPageSortByTypes } from 'constants/offerListFilters'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'

import styled from 'styled-components'
import { rem } from 'polished'

import LayoutContainer from 'components/Common/LayoutContainer'

interface Props {
  filters: App.OfferListFilters;
  isEnabled?: boolean;
  regionCode?: string;
  banners: Array<App.Banner>;
  fetchBanners: Utils.ActionDispatcher<typeof fetchBanners>;
}

const getBannerPlacements = memoize((banners: Array<App.Banner>) => ({
  top: getPlacement(banners, 'top'),
}))

const types: Array<App.OfferType> = [OFFER_TYPE_LAST_MINUTE]

const TypesPageContainer = styled(LayoutContainer)`
  padding-top: ${rem(16)};
  padding-bottom: ${rem(16)};

  > * + * {
    margin-top: ${rem(12)};
  }
`

function LastMinutesEscapesPage(props: Props) {
  const { filters, isEnabled, regionCode, banners, fetchBanners } = props
  const bannerPlacements = getBannerPlacements(banners)

  useEffect(() => {
    fetchBanners(BANNER_LOCATION_LAST_MINUTE)
  }, [fetchBanners])

  const finalFilters = useMemo(() => {
    return {
      ...filters,
      offerTypes: types,
      frontPageVisibleOnly: true,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters])

  const onAnalyticEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position } = dispatchAction
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index: position,
        list: 'last-minute-escapes',
      })
    }
  }, [])

  if (!isEnabled) {
    return <HttpStatus code={404}>
      <Error404Page invalidRegion />
    </HttpStatus>
  }

  const title = (config.BRAND === 'luxuryescapes' ? `Last Minute Luxury Hotel & Accommodation Deals - ${config.title} ${regionCode}` : `Last Minute Escapes - ${config.title} ${regionCode}`)
  const description = (config.BRAND === 'luxuryescapes' ? 'Deals on luxury hotels and accommodation with exclusive inclusions for even more lux. Book online now for travel in the next 2 weeks.' : undefined)

  const offerListExtras: Array<OfferListV2Item> = [{
    position: 1,
    id: 'leaderboard',
    element: <Leaderboard banners={bannerPlacements.top} />,
    show: bannerPlacements.top.length > 0,
  }]

  return <div>
    <HeadData title={title} description={description}/>
    <LastMinuteHero />
    <TypesPageContainer>
      <OfferListFrontPageFilters
        types={types}
        existingFilters={filters}
        sortOptions={frontPageSortByTypes}
        mapViewPath={`/${OFFER_TYPE_LAST_MINUTE_SLUG}/map-view`}
      />
      <OfferListEventsProvider onListEvent={onAnalyticEvent}>
        <OfferList
          filters={finalFilters}
          interstitials={offerListExtras}
        />
      </OfferListEventsProvider>
    </TypesPageContainer>
  </div>
}

const mapStateToProps = (state: App.State) => ({
  isEnabled: isLastMinuteEnabled(state),
  filters: getUrlOfferListFilters(state),
  regionCode: state.geo.currentRegionCode,
  banners: state.content.banners.lastMinute,
})

const mapDispatchToProps = {
  fetchBanners,
}

export default connect(mapStateToProps, mapDispatchToProps)(LastMinutesEscapesPage)
